import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProductIcon } from "@/components/ProductIcon";
import {
  ActivityIcon,
  BookIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  FileTextIcon,
  HeadsetIcon,
  LibraryIcon,
  LifeBuoyIcon,
  MailIcon,
  PhoneIcon,
  PrinterIcon,
} from "@/components/report/icons";
import {
  AmberAlert,
  NordicPackNotice,
  OptionalBadge,
  ReportLink,
  ReportSection,
  ReportSectionHeader,
  StepBody,
  StepCard,
} from "@/components/report/primitives";
import { useApp } from "@/context/AppContext";
import { asList, hasProduct } from "@/engine/answers";
import { isNordicLocale } from "@/config/loadConfig";
import { localizedKnowledgeBaseUrl } from "@/config/localizedUrls";
import {
  licenseQuestionKey,
  loginQuestionKey,
  visibleQuestionIds,
} from "@/engine/questions";
import { createReportId } from "@/engine/report";
import { trackEvent } from "@/analytics";

function formatAnswer(
  t: (key: string) => string,
  id: string,
  value: string | string[] | undefined,
): string {
  const labelFor = (item: string) => {
    const nested = t(`wizard.steps.${id}.options.${item}.label`);
    if (nested && !nested.startsWith("wizard.steps")) return nested;
    const flat = t(`wizard.steps.${id}.options.${item}`);
    return flat.startsWith("wizard.steps") ? item : flat;
  };
  if (Array.isArray(value)) return value.map(labelFor).join(", ");
  if (!value) return "";
  return labelFor(value);
}

export function ReportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { config, answers, locale, resetAnswers, resolveLink, resolveString } = useApp();
  const downloads = config.edition.downloads;
  const reportId = useMemo(() => createReportId(), []);
  const nordic = isNordicLocale(locale);
  const rwVersions = asList(answers.rw_version);
  const mvVersions = asList(answers.mv_version);
  const mvAssistPlatforms = asList(answers.mv_assist_platforms);
  const tasksOs = asList(answers.tasks_os);
  const hasRw = hasProduct(answers, "rw");
  const hasClaro = hasProduct(answers, "claroread");
  const hasMv = hasProduct(answers, "mindview");
  const hasTasks = hasProduct(answers, "tasks");
  const isGroup = answers.rw_license === "group";
  const isEnterprise = answers.rw_license === "enterprise";
  const isSingle = answers.rw_license === "single";
  const rwWindowsDownloadHref = localizedKnowledgeBaseUrl(downloads.rwWindowsKnowledgeBase, locale);
  const rwMacDownloadHref = localizedKnowledgeBaseUrl(downloads.rwMacKnowledgeBase, locale);
  const rwIosSystemRequirementsHref = localizedKnowledgeBaseUrl(downloads.rwIosSystemRequirements, locale);
  const rwIosDownloadHref = localizedKnowledgeBaseUrl(downloads.rwIosKnowledgeBase, locale);
  const rwIosMdmGuideHref = localizedKnowledgeBaseUrl(downloads.rwIosMdmGuide, locale);
  const rwIosAllowlistHref = localizedKnowledgeBaseUrl(downloads.rwIosAllowlist, locale);
  const rwAndroidSystemRequirementsHref = localizedKnowledgeBaseUrl(downloads.rwAndroidSystemRequirements, locale);
  const rwAndroidDownloadHref = localizedKnowledgeBaseUrl(downloads.rwAndroidKnowledgeBase, locale);
  const rwAndroidDeploymentGuideHref = localizedKnowledgeBaseUrl(downloads.rwAndroidDeploymentGuide, locale);
  const rwMacSingleActivationHref = localizedKnowledgeBaseUrl(
    "https://everwayeu.zendesk.com/hc/en-gb/articles/56554647793043-Installing-Read-Write-Mac-For-The-First-Time",
    locale,
  );
  const mindviewWindowsDownloadHref = localizedKnowledgeBaseUrl(
    downloads.mindviewWindowsKnowledgeBase,
    locale,
  );
  const mindviewMacDownloadHref = localizedKnowledgeBaseUrl(
    downloads.mindviewMacKnowledgeBase,
    locale,
  );
  const mindviewAssistAndroidSystemRequirementsHref = localizedKnowledgeBaseUrl(
    downloads.mindviewAssistAndroidSystemRequirements,
    locale,
  );
  const mindviewAssistAndroidDownloadHref = localizedKnowledgeBaseUrl(
    downloads.mindviewAssistAndroidKnowledgeBase,
    locale,
  );
  const mindviewAssistIosSystemRequirementsHref = localizedKnowledgeBaseUrl(
    downloads.mindviewAssistIosSystemRequirements,
    locale,
  );
  const mindviewAssistIosDownloadHref = localizedKnowledgeBaseUrl(
    downloads.mindviewAssistIosKnowledgeBase,
    locale,
  );
  const tasksWindowsDownloadHref = localizedKnowledgeBaseUrl(
    downloads.tasksWindowsKnowledgeBase,
    locale,
  );
  const tasksMacDownloadHref = localizedKnowledgeBaseUrl(
    downloads.tasksMacKnowledgeBase,
    locale,
  );
  const visible = visibleQuestionIds(answers);

  const [summaryOpen, setSummaryOpen] = useState(false);
  const [licenseOpen, setLicenseOpen] = useState(false);
  const [claroOpen, setClaroOpen] = useState(false);
  const [rwWinOpen, setRwWinOpen] = useState(false);
  const [rwMacOpen, setRwMacOpen] = useState(false);
  const [rwChromeOpen, setRwChromeOpen] = useState(false);
  const [rwIosOpen, setRwIosOpen] = useState(false);
  const [rwAndroidOpen, setRwAndroidOpen] = useState(false);
  const [mvWinOpen, setMvWinOpen] = useState(false);
  const [mvMacOpen, setMvMacOpen] = useState(false);
  const [mvOnlineOpen, setMvOnlineOpen] = useState(false);
  const [mvAssistAndroidOpen, setMvAssistAndroidOpen] = useState(false);
  const [mvAssistIosOpen, setMvAssistIosOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);

  const showLicensing = isGroup || (isEnterprise && hasMv);
  const showLicenseToggle = (isGroup && (hasRw || hasClaro || hasMv)) || (isEnterprise && hasMv);

  const expandAll = () => {
    setSummaryOpen(true);
    setLicenseOpen(true);
    setClaroOpen(true);
    setRwWinOpen(true);
    setRwMacOpen(true);
    setRwChromeOpen(true);
    setRwIosOpen(true);
    setRwAndroidOpen(true);
    setMvWinOpen(true);
    setMvMacOpen(true);
    setMvOnlineOpen(true);
    setMvAssistAndroidOpen(true);
    setMvAssistIosOpen(true);
    setTasksOpen(true);
  };

  const printReport = () => {
    const previous = {
      summaryOpen,
      licenseOpen,
      claroOpen,
      rwWinOpen,
      rwMacOpen,
      rwChromeOpen,
      rwIosOpen,
      rwAndroidOpen,
      mvWinOpen,
      mvMacOpen,
      mvOnlineOpen,
      mvAssistAndroidOpen,
      mvAssistIosOpen,
      tasksOpen,
    };
    expandAll();
    setTimeout(() => {
      window.print();
      setSummaryOpen(previous.summaryOpen);
      setLicenseOpen(previous.licenseOpen);
      setClaroOpen(previous.claroOpen);
      setRwWinOpen(previous.rwWinOpen);
      setRwMacOpen(previous.rwMacOpen);
      setRwChromeOpen(previous.rwChromeOpen);
      setRwIosOpen(previous.rwIosOpen);
      setRwAndroidOpen(previous.rwAndroidOpen);
      setMvWinOpen(previous.mvWinOpen);
      setMvMacOpen(previous.mvMacOpen);
      setMvOnlineOpen(previous.mvOnlineOpen);
      setMvAssistAndroidOpen(previous.mvAssistAndroidOpen);
      setMvAssistIosOpen(previous.mvAssistIosOpen);
      setTasksOpen(previous.tasksOpen);
    }, 100);
  };

  const emailReport = () => {
    const productLabels = formatAnswer(t, "products", answers.products);
    const versionLabels = formatAnswer(t, "rw_version", answers.rw_version);
    const qa = visible
      .filter((id) => id !== "products")
      .map((id) => {
        const question =
          id === "rw_license"
            ? t(`wizard.steps.rw_license.${licenseQuestionKey(answers)}`)
            : id === "login_method"
              ? t(`wizard.steps.login_method.${loginQuestionKey(answers)}`)
              : t(`wizard.steps.${id}.question`);
        return `${question}\n→ ${formatAnswer(t, id, answers[id])}`;
      })
      .join("\n\n");
    const subject = encodeURIComponent(
      `${t("report.buttons.emailSubject")} - ${config.app.customerType} - ${productLabels}`,
    );
    const body = encodeURIComponent(
      `${t("report.buttons.emailSubject")}\n\n${t("report.buttons.emailOrganization")}: ${config.app.customerType}\n${t("report.buttons.emailProducts")}: ${productLabels}\n${t("report.buttons.emailVersion")}: ${versionLabels}\n\n${qa}\n\n${t("report.buttons.emailReportId")}: ${reportId}`,
    );
    trackEvent("report_emailed");
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  let licenseStep = 1;
  let rwWinStep = 1;
  let rwMacStep = 1;
  let rwChromeStep = 1;
  let rwIosStep = 1;
  let rwAndroidStep = 1;
  let mvWinStep = 1;
  let mvMacStep = 1;
  let mvOnlineStep = 1;
  let mvAssistAndroidStep = 1;
  let mvAssistIosStep = 1;
  let tasksStep = 1;

  return (
    <div className="min-h-screen page-bg flex flex-col items-center px-4 py-12">
      <div className="report-surface max-w-xl w-full flex-grow flex flex-col">
        <div className="animate-in fade-in duration-500 flex-grow flex flex-col items-center text-center pt-4">
          <div className="w-14 h-14 bg-green-500/10 text-green-400 rounded-full flex items-center justify-center mb-6 border border-green-500/20">
            <CheckCircleIcon className="w-7 h-7" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">{t("report.title")}</h1>
          <p className="text-muted-foreground text-base mb-8 max-w-lg leading-relaxed">
            {t("report.subtitle")}
          </p>

          <div className="w-full space-y-6 text-left">
            <div className="rounded-xl border border-ink-10 overflow-hidden">
              <div
                className="px-5 py-3 border-b border-ink-10 flex items-center gap-3 cursor-pointer"
                onClick={() => setSummaryOpen((open) => !open)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSummaryOpen((open) => !open);
                  }
                }}
                role="button"
                tabIndex={0}
                data-testid="toggle-summary"
                aria-expanded={summaryOpen}
              >
                <CheckCircleIcon className="w-4 h-4 text-green-500 shrink-0" />
                <h3 className="font-semibold text-sm flex-grow">{t("report.summary.title")}</h3>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground/60">
                    {summaryOpen ? t("report.summary.collapse") : t("report.summary.expand")}
                  </span>
                  <div className={`transition-transform duration-200 ${summaryOpen ? "rotate-90" : ""}`}>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground/60" />
                  </div>
                </div>
              </div>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  summaryOpen ? "max-h-[600px]" : "max-h-0"
                }`}
              >
                <div className="px-5 py-3 space-y-1.5">
                  {visible
                    .filter((id) => id !== "products")
                    .map((id) => (
                      <div
                        key={id}
                        className="flex items-center justify-between px-3 py-2 rounded-lg border border-ink-10 gap-4"
                      >
                        <p className="text-xs text-muted-foreground">
                          {id === "rw_license"
                            ? t(`wizard.steps.rw_license.${licenseQuestionKey(answers)}`)
                            : id === "login_method"
                              ? t(`wizard.steps.login_method.${loginQuestionKey(answers)}`)
                              : t(`wizard.steps.${id}.question`)}
                        </p>
                        <p className="font-medium text-sm flex items-center gap-2">
                          <CheckCircleIcon className="w-3.5 h-3.5 text-green-500 shrink-0" />
                          {formatAnswer(t, id, answers[id])}
                        </p>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {showLicensing ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.licensing.title")}
                  subtitle={isGroup ? t("report.licensing.groupLicense") : undefined}
                  icon={
                    <div className="w-7 h-7 rounded-lg bg-ink-5 border border-ink-10 flex items-center justify-center shrink-0">
                      <BriefcaseIcon className="w-4 h-4 text-ink-60" />
                    </div>
                  }
                  expanded={licenseOpen}
                  onToggle={() => setLicenseOpen((open) => !open)}
                  toggleTestId="toggle-license-expand"
                  showToggle={showLicenseToggle}
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  {isGroup && (hasRw || hasClaro || hasMv) ? (
                    <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                      {t("report.licensing.required")}
                    </p>
                  ) : null}
                  {isGroup && (hasRw || hasClaro) ? (
                    <StepCard
                      number={licenseStep++}
                      title={
                        <>
                          <ProductIcon
                            name={hasClaro && !hasRw ? "claroread" : "rw"}
                            className="w-4 h-4 rounded shrink-0"
                          />
                          {t("report.licensing.adminTool.title")}
                        </>
                      }
                      expanded={licenseOpen}
                      maxHeightClass="max-h-52"
                    >
                      <StepBody>{t("report.licensing.adminTool.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(1, "https://everwayeu.zendesk.com/hc/en-gb/articles/52749939515923-Read-Write-for-Work-Everway-Admin-Tool-User-Guide")}
                        testId="link-admin-tool"
                      >
                        {t("report.licensing.adminTool.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}

                  {hasMv && (isGroup || isEnterprise) ? (
                    <StepCard
                      number={licenseStep++}
                      title={t("report.licensing.mvUserMgmt.title")}
                      expanded={licenseOpen}
                      maxHeightClass="max-h-64"
                    >
                      <StepBody>{t("report.licensing.mvUserMgmt.description")}</StepBody>
                      <AmberAlert>
                        <p>{t("report.licensing.mvUserMgmt.invitationNote")}</p>
                      </AmberAlert>
                      <ReportLink
                        href={resolveLink(2, "https://everwayeu.zendesk.com/hc/en-gb/articles/53002667934995-MindView-Accounts-Portal-User-Management")}
                        testId="link-mv-user-management"
                      >
                        {t("report.licensing.mvUserMgmt.guideLink")}
                      </ReportLink>
                      <ReportLink
                        href={downloads.mindviewUserManagement}
                        testId="link-mv-user-management-console"
                        className="flex items-center gap-1.5 mt-1 text-xs font-medium text-ink-80 hover:text-ink transition-colors"
                      >
                        {t("report.licensing.mvUserMgmt.consoleLink")}
                      </ReportLink>
                    </StepCard>
                  ) : null}

                  {hasTasks && isGroup ? (
                    <StepCard
                      number={licenseStep++}
                      title={
                        <>
                          <ProductIcon name="tasks" className="w-4 h-4 rounded shrink-0" />
                          {t("report.licensing.tasks.title")}
                          <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-[10px] font-semibold text-emerald-400 tracking-wide shrink-0">
                            {t("report.licensing.tasks.badge")}
                          </span>
                        </>
                      }
                      expanded={licenseOpen}
                      maxHeightClass="max-h-24"
                      testId="tasks-license-info"
                    >
                      <p className="text-[13px] text-foreground/60 leading-relaxed">
                        {t("report.licensing.tasks.description")}{" "}
                        <a
                          href={downloads.tasks}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                          data-testid="link-tasks-url"
                        >
                          {downloads.tasks.replace(/^https?:\/\//, "")}
                        </a>{" "}
                        {t("report.licensing.tasks.descriptionEnd")}
                      </p>
                    </StepCard>
                  ) : null}
                </div>
              </ReportSection>
            ) : null}

            {hasClaro ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.claroRead.title")}
                  icon={<ProductIcon name="claroread" className="w-7 h-7 rounded shrink-0" />}
                  expanded={claroOpen}
                  onToggle={() => setClaroOpen((open) => !open)}
                  toggleTestId="toggle-claroread-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.claroRead.subtitle")}
                  </p>
                  <StepCard number={1} title={t("report.claroRead.sysReq.title")} expanded={claroOpen}>
                    <StepBody>{t("report.claroRead.sysReq.description")}</StepBody>
                    <ReportLink href="#" testId="link-claroread-sysreq">
                      {t("report.claroRead.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={2}
                    title={t("report.claroRead.download.title")}
                    expanded={claroOpen}
                  >
                    <StepBody>{t("report.claroRead.download.description")}</StepBody>
                    <ReportLink href="#" testId="link-claroread-download" download>
                      {t("report.claroRead.download.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={3} title={t("report.claroRead.deploy.title")} expanded={claroOpen}>
                    <StepBody>{t("report.claroRead.deploy.description")}</StepBody>
                    <ReportLink href="#" testId="link-claroread-deploy">
                      {t("report.claroRead.deploy.link")}
                    </ReportLink>
                  </StepCard>
                </div>
              </ReportSection>
            ) : null}

            {hasRw && isSingle && rwVersions.includes("win") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.rwSingle.title")}
                  icon={<ProductIcon name="rw" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["windows"]}
                  expanded
                  onToggle={() => undefined}
                  showToggle={false}
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.rwSingle.subtitle")}
                  </p>
                  <StepCard number={1} title={t("report.rwWindows.sysReq.title")} expanded={false}>
                    <StepBody>{t("report.rwWindows.sysReq.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(3, "https://everwayeu.zendesk.com/hc/en-gb/articles/52759390419091-Read-Write-For-Windows-System-Requirements")}
                      testId="link-single-sys-req"
                    >
                      {t("report.rwWindows.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={2}
                    title={t("report.rwWindows.download.knowledgeBase.title")}
                    expanded={false}
                    maxHeightClass="max-h-96"
                  >
                    <StepBody>{t("report.rwWindows.download.knowledgeBase.description")}</StepBody>
                    <AmberAlert title={t("report.rwWindows.download.knowledgeBase.singleTitle")}>
                      {t("report.rwWindows.download.knowledgeBase.singleInstruction")}
                    </AmberAlert>
                    <ReportLink href={rwWindowsDownloadHref} testId="download-single-exe">
                      {t("report.rwWindows.download.knowledgeBase.link")}
                    </ReportLink>
                    <div className="mt-3 border-t border-ink-10 pt-3">
                      <div className="border-l-2 border-ink-30 pl-3 space-y-1.5">
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {t("report.rwWindows.download.singleBrowserExtNote")}
                        </p>
                        <ReportLink
                          href={resolveLink(
                            37,
                            "https://everwayeu.zendesk.com/hc/en-gb/articles/53206080568723-What-is-the-Support-for-Read-Write-Desktop-browser-extension",
                          )}
                          testId="link-single-browser-extension"
                        >
                          {t("report.rwWindows.download.singleBrowserExtLink")}
                        </ReportLink>
                      </div>
                    </div>
                    {nordic ? (
                      <NordicPackNotice
                        title={t("report.rwWindows.nordicPack.title")}
                        body={t("report.rwWindows.nordicPack.body")}
                        linkLabel={t("report.rwWindows.nordicPack.link")}
                        href={downloads.rwNordicLanguagePack}
                        testId="download-nordic-pack-single"
                      />
                    ) : null}
                  </StepCard>
                  <StepCard
                    number={3}
                    title={t("report.rwSingle.activate.title")}
                    expanded={false}
                    maxHeightClass="max-h-64"
                  >
                    <StepBody>{t("report.rwSingle.activate.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(4, "https://everwayeu.zendesk.com/hc/en-gb/articles/53206021985811-Activate-Read-Write-for-Windows-Using-Your-Product-Code")}
                      testId="link-activation-guide"
                    >
                      {t("report.rwSingle.activate.articleLink")}
                    </ReportLink>
                    <div className="flex items-start gap-2 mt-2 rounded-lg bg-amber-500/[0.08] border border-amber-500/20 px-3 py-2">
                      <p className="text-[12px] text-amber-300/80 leading-relaxed">
                        {t("report.rwSingle.activate.productCodeTip")}
                      </p>
                    </div>
                  </StepCard>
                </div>
              </ReportSection>
            ) : null}

            {hasRw && isSingle && rwVersions.includes("mac") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.rwSingleMac.title")}
                  icon={<ProductIcon name="rw" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["macos"]}
                  expanded
                  onToggle={() => undefined}
                  showToggle={false}
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.rwSingleMac.subtitle")}
                  </p>
                  <StepCard number={1} title={t("report.rwSingleMac.sysReq.title")} expanded={false}>
                    <StepBody>{t("report.rwSingleMac.sysReq.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(10, "https://everwayeu.zendesk.com/hc/en-gb/articles/53281500196755-Read-Write-Mac-System-requirements")}
                      testId="link-single-mac-sys-req"
                    >
                      {t("report.rwSingleMac.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={2}
                    title={t("report.rwSingleMac.download.title")}
                    expanded={false}
                  >
                    <StepBody>{t("report.rwSingleMac.download.description")}</StepBody>
                    <ReportLink href={rwMacDownloadHref} testId="download-single-mac-dmg">
                      {t("report.rwSingleMac.download.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={3}
                    title={t("report.rwSingleMac.activate.title")}
                    expanded={false}
                    maxHeightClass="max-h-64"
                  >
                    <StepBody>{t("report.rwSingleMac.activate.description")}</StepBody>
                    <ReportLink
                      href={rwMacSingleActivationHref}
                      testId="link-single-mac-activation-guide"
                    >
                      {t("report.rwSingleMac.activate.articleLink")}
                    </ReportLink>
                    <div className="flex items-start gap-2 mt-2 rounded-lg bg-amber-500/[0.08] border border-amber-500/20 px-3 py-2">
                      <p className="text-[12px] text-amber-300/80 leading-relaxed">
                        {t("report.rwSingleMac.activate.productCodeTip")}
                      </p>
                    </div>
                  </StepCard>
                </div>
              </ReportSection>
            ) : null}

            {hasRw && !isSingle && rwVersions.includes("win") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.rwWindows.title")}
                  icon={<ProductIcon name="rw" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["windows"]}
                  expanded={rwWinOpen}
                  onToggle={() => setRwWinOpen((open) => !open)}
                  toggleTestId="toggle-rw-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.rwWindows.subtitle")}
                  </p>
                  <StepCard number={rwWinStep++} title={t("report.rwWindows.sysReq.title")} expanded={rwWinOpen}>
                    <StepBody>{t("report.rwWindows.sysReq.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(3, "https://everwayeu.zendesk.com/hc/en-gb/articles/52759390419091-Read-Write-For-Windows-System-Requirements")}
                      testId="link-system-requirements"
                    >
                      {t("report.rwWindows.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={rwWinStep++}
                    title={t("report.rwWindows.download.knowledgeBase.title")}
                    expanded={rwWinOpen}
                    maxHeightClass="max-h-96"
                  >
                    <StepBody>{t("report.rwWindows.download.knowledgeBase.description")}</StepBody>
                    <AmberAlert title={t("report.rwWindows.download.knowledgeBase.groupTitle")}>
                      {t("report.rwWindows.download.knowledgeBase.groupInstruction")}
                    </AmberAlert>
                    <ReportLink href={rwWindowsDownloadHref} testId="download-msi">
                      {t("report.rwWindows.download.knowledgeBase.link")}
                    </ReportLink>
                    <div className="border-l-2 border-amber-500/40 pl-3 space-y-1">
                      <ReportLink
                        href={resolveLink(5, "https://everwayeu.zendesk.com/hc/en-gb/articles/53205995394323-Read-Write-for-Work-Windows-MSI-Installation-Parameters")}
                        testId="link-msi-docs"
                        className="flex items-center gap-1.5 text-xs font-medium text-foreground/50 hover:text-ink-70 transition-colors"
                      >
                        {t("report.rwWindows.download.docsLink")}
                      </ReportLink>
                      <p className="text-[11px] text-amber-300/85 italic leading-relaxed">
                        {t("report.rwWindows.download.docsWarning")}
                      </p>
                    </div>
                    {nordic ? (
                      <NordicPackNotice
                        title={t("report.rwWindows.nordicPack.title")}
                        body={t("report.rwWindows.nordicPack.body")}
                        linkLabel={t("report.rwWindows.nordicPack.link")}
                        href={downloads.rwNordicLanguagePack}
                        testId="download-nordic-pack"
                      />
                    ) : null}
                  </StepCard>
                  <StepCard number={rwWinStep++} title={t("report.rwWindows.browserExt.title")} expanded={rwWinOpen} maxHeightClass="max-h-96">
                    <StepBody>{t("report.rwWindows.browserExt.description")}</StepBody>
                    <div className="border-l-2 border-amber-500/40 pl-3 space-y-2">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {t("report.rwWindows.browserExt.distinctionNote")}
                      </p>
                    </div>
                    <StepBody>{t("report.rwWindows.browserExt.parametersDescription")}</StepBody>
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      <ReportLink
                        href={resolveLink(
                          36,
                          "https://everwayeu.zendesk.com/hc/en-gb/articles/56590115114131-Read-Write-for-Workplace-Windows-Deploy-the-Support-for-Read-Write-Desktop-browser-extension",
                        )}
                        testId="link-browser-extension-deployment"
                      >
                        {t("report.rwWindows.browserExt.link")}
                      </ReportLink>
                      <ReportLink
                        href={resolveLink(
                          37,
                          "https://everwayeu.zendesk.com/hc/en-gb/articles/53206080568723-What-is-the-Support-for-Read-Write-Desktop-browser-extension",
                        )}
                        testId="link-browser-extension-info"
                      >
                        {t("report.rwWindows.browserExt.secondaryLink")}
                      </ReportLink>
                    </div>
                  </StepCard>
                  {answers.network_restricted === "yes" ? (
                    <StepCard number={rwWinStep++} title={t("report.rwWindows.allowlist.title")} expanded={rwWinOpen}>
                      <StepBody>{t("report.rwWindows.allowlist.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(6, "https://everwayeu.zendesk.com/hc/en-gb/articles/53244752488339-URLs-Used-By-Read-Write-For-Windows")}
                        testId="link-url-allowlist"
                      >
                        {t("report.rwWindows.allowlist.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {answers.login_method === "auto" ? (
                    <StepCard
                      number={rwWinStep++}
                      title={t("report.rwWindows.autoSignIn.title")}
                      expanded={rwWinOpen}
                      maxHeightClass="max-h-52"
                    >
                      <StepBody>{t("report.rwWindows.autoSignIn.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(
                          33,
                          "https://everwayeu.zendesk.com/hc/en-gb/articles/53205995254547-Read-Write-for-Workplace-Windows-Skip-sign-in-with-the-Multi-User-Setup-Tool",
                        )}
                        testId="link-auto-signin"
                      >
                        {t("report.rwWindows.autoSignIn.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {answers.offline === "yes" ? (
                    <StepCard
                      number={rwWinStep++}
                      title={t("report.rwWindows.offline.title")}
                      expanded={rwWinOpen}
                      maxHeightClass="max-h-64"
                    >
                      <StepBody>{t("report.rwWindows.offline.description")}</StepBody>
                      <AmberAlert title={t("report.rwWindows.offline.alertTitle")}>
                        <p>
                          Contact{" "}
                          <span className="font-semibold text-amber-300/90">
                            {t("report.rwWindows.offline.everwaySupportLabel")}
                          </span>{" "}
                          to request your offline licence. You will receive a file named{" "}
                          <span className="font-mono font-semibold text-amber-300/90">
                            {t("report.rwWindows.offline.fileLabel")}
                          </span>{" "}
                          — do not rename it, as changing the filename will prevent activation.
                        </p>
                      </AmberAlert>
                      <ReportLink
                        href={resolveLink(9, "https://everwayeu.zendesk.com/hc/en-gb/articles/56398267738387-Offline-Use-of-Read-Write-for-Work-Windows")}
                        testId="link-offline-setup"
                      >
                        {t("report.rwWindows.offline.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  <div className="flex items-start gap-2 px-1 pt-1">
                    <div className="flex-grow space-y-1">
                      <p className="text-xs text-muted-foreground/50">
                        {t("report.rwWindows.legacy.label")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("report.rwWindows.legacy.description")}
                      </p>
                    </div>
                    <ReportLink
                      href={rwWindowsDownloadHref}
                      testId="link-rw-legacy-guides-group"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-80 hover:text-ink transition-colors shrink-0"
                    >
                      {t("report.rwWindows.legacy.link")}
                    </ReportLink>
                  </div>
                </div>
              </ReportSection>
            ) : null}

            {hasRw && !isSingle && rwVersions.includes("mac") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.rwMac.title")}
                  icon={<ProductIcon name="rw" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["macos"]}
                  expanded={rwMacOpen}
                  onToggle={() => setRwMacOpen((open) => !open)}
                  toggleTestId="toggle-rwmac-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.rwMac.subtitle")}
                  </p>
                  <StepCard number={rwMacStep++} title={t("report.rwMac.sysReq.title")} expanded={rwMacOpen}>
                    <StepBody>{t("report.rwMac.sysReq.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(10, "https://everwayeu.zendesk.com/hc/en-gb/articles/53281500196755-Read-Write-Mac-System-requirements")}
                      testId="link-mac-system-requirements"
                    >
                      {t("report.rwMac.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={rwMacStep++}
                    title={t("report.rwMac.download.title")}
                    expanded={rwMacOpen}
                  >
                    <StepBody>{t("report.rwMac.download.description")}</StepBody>
                    <ReportLink href={rwMacDownloadHref} testId="download-mac-pkg">
                      {t("report.rwMac.download.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={rwMacStep++} title={t("report.rwMac.deploy.title")} expanded={rwMacOpen}>
                    <StepBody>{t("report.rwMac.deploy.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(11, "https://everwayeu.zendesk.com/hc/en-gb/articles/53281574321427-Deploying-Read-Write-For-Mac#deployment-b")}
                      testId="link-mac-deploy-guide"
                    >
                      {t("report.rwMac.deploy.link")}
                    </ReportLink>
                  </StepCard>
                  {answers.network_restricted === "yes" ? (
                    <StepCard number={rwMacStep++} title={t("report.rwMac.allowlist.title")} expanded={rwMacOpen}>
                      <StepBody>{t("report.rwMac.allowlist.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(12, "https://everwayeu.zendesk.com/hc/en-gb/articles/53206080488339-URLs-Used-By-Read-Write-For-Mac")}
                        testId="link-mac-url-allowlist"
                      >
                        {t("report.rwMac.allowlist.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {answers.login_method === "auto" ? (
                    <StepCard number={rwMacStep++} title={t("report.rwMac.autoSignIn.title")} expanded={rwMacOpen}>
                      <StepBody>{t("report.rwMac.autoSignIn.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(7, "https://everwayeu.zendesk.com/hc/en-gb/articles/53206036329107-How-To-Configure-Automatic-Login-For-Your-Users")}
                        testId="link-mac-auto-signin"
                      >
                        {t("report.rwMac.autoSignIn.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {answers.use_on_webpages === "yes" ? (
                    <StepCard number={rwMacStep++} title={t("report.rwMac.browserExt.title")} expanded={rwMacOpen}>
                      <StepBody>{t("report.rwMac.browserExt.description")}</StepBody>
                    </StepCard>
                  ) : null}
                  {answers.offline === "yes" ? (
                    <StepCard number={rwMacStep++} title={t("report.rwMac.offline.title")} expanded={rwMacOpen}>
                      <StepBody>{t("report.rwMac.offline.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(13, "https://everwayeu.zendesk.com/hc/en-gb/articles/53205976141075-Activating-Read-Write-For-Mac-Offline")}
                        testId="link-mac-offline-setup"
                      >
                        {t("report.rwMac.offline.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {nordic ? (
                    <NordicPackNotice
                      title={t("report.rwWindows.nordicPack.title")}
                      body={t("report.rwWindows.nordicPack.body")}
                      linkLabel={t("report.rwWindows.nordicPack.link")}
                      href={downloads.rwNordicLanguagePack}
                      testId="download-nordic-pack-mac"
                    />
                  ) : null}
                </div>
              </ReportSection>
            ) : null}

            {hasRw && !isSingle && rwVersions.includes("chrome_edge") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.rwChrome.title")}
                  icon={<ProductIcon name="rw" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["chrome", "edge"]}
                  expanded={rwChromeOpen}
                  onToggle={() => setRwChromeOpen((open) => !open)}
                  toggleTestId="toggle-rwchrome-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.rwChrome.subtitle")}
                  </p>
                  <StepCard number={rwChromeStep++} title={t("report.rwChrome.sysReq.title")} expanded={rwChromeOpen}>
                    <StepBody>{t("report.rwChrome.sysReq.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(14, "https://everwayeu.zendesk.com/hc/en-gb/articles/53244961471635-Read-Write-for-Work-Google-Chrome-Minimum-Requirements")}
                      testId="link-chrome-system-requirements"
                    >
                      {t("report.rwChrome.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={rwChromeStep++} title={t("report.rwChrome.chrome.title")} expanded={rwChromeOpen}>
                    <StepBody>{t("report.rwChrome.chrome.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(15, "https://everwayeu.zendesk.com/hc/en-gb/articles/53244974255507-Deploying-Read-Write-for-Work-Google-Chrome")}
                      testId="link-chrome-deploy-guide"
                    >
                      {t("report.rwChrome.chrome.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={rwChromeStep++} title={t("report.rwChrome.edge.title")} expanded={rwChromeOpen}>
                    <StepBody>{t("report.rwChrome.edge.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(16, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245006068883-Deploying-Read-Write-For-Work-Microsoft-Edge")}
                      testId="link-edge-deploy-guide"
                    >
                      {t("report.rwChrome.edge.link")}
                    </ReportLink>
                  </StepCard>
                  {answers.network_restricted === "yes" ? (
                    <StepCard
                      number={rwChromeStep++}
                      title={t("report.rwChrome.allowlist.title")}
                      expanded={rwChromeOpen}
                      maxHeightClass="max-h-52"
                    >
                      <StepBody>{t("report.rwChrome.allowlist.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(17, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245062398867-URLs-Used-By-Read-Write-For-Work-Google-Chrome")}
                        testId="link-chrome-url-allowlist"
                      >
                        {t("report.rwChrome.allowlist.chromeLink")}
                      </ReportLink>
                      <ReportLink
                        href={resolveLink(18, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245042949779-URLs-Used-By-Read-Write-For-Work-Microsoft-Edge")}
                        testId="link-edge-url-allowlist"
                        className="inline-flex items-center gap-1.5 mt-2 ml-4 text-xs font-medium text-ink-80 hover:text-ink transition-colors"
                      >
                        {t("report.rwChrome.allowlist.edgeLink")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                </div>
              </ReportSection>
            ) : null}

            {hasRw && !isSingle && rwVersions.includes("ios") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.rwIos.title")}
                  icon={<ProductIcon name="rw" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["ios"]}
                  expanded={rwIosOpen}
                  onToggle={() => setRwIosOpen((open) => !open)}
                  toggleTestId="toggle-rw-ios-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.rwIos.subtitle")}
                  </p>
                  <StepCard number={rwIosStep++} title={t("report.rwIos.sysReq.title")} expanded={rwIosOpen}>
                    <StepBody>{t("report.rwIos.sysReq.description")}</StepBody>
                    <ReportLink href={rwIosSystemRequirementsHref} testId="link-rw-ios-system-requirements">
                      {t("report.rwIos.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={rwIosStep++} title={t("report.rwIos.download.title")} expanded={rwIosOpen}>
                    <StepBody>{t("report.rwIos.download.description")}</StepBody>
                    <ReportLink href={rwIosDownloadHref} testId="link-rw-ios-download">
                      {t("report.rwIos.download.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={rwIosStep++} title={t("report.rwIos.mdm.title")} expanded={rwIosOpen}>
                    <StepBody>{t("report.rwIos.mdm.description")}</StepBody>
                    <ReportLink href={rwIosMdmGuideHref} testId="link-rw-ios-mdm">
                      {t("report.rwIos.mdm.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={rwIosStep++} title={t("report.rwIos.userGuide.title")} expanded={rwIosOpen}>
                    <StepBody>{t("report.rwIos.userGuide.description")}</StepBody>
                    <ReportLink href={downloads.rwIosUserGuide} testId="link-rw-ios-user-guide">
                      {t("report.rwIos.userGuide.link")}
                    </ReportLink>
                  </StepCard>
                  {answers.network_restricted === "yes" ? (
                    <StepCard number={rwIosStep++} title={t("report.rwIos.allowlist.title")} expanded={rwIosOpen}>
                      <StepBody>{t("report.rwIos.allowlist.description")}</StepBody>
                      <ReportLink href={rwIosAllowlistHref} testId="link-rw-ios-allowlist">
                        {t("report.rwIos.allowlist.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                </div>
              </ReportSection>
            ) : null}

            {hasRw && !isSingle && rwVersions.includes("android") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.rwAndroid.title")}
                  icon={<ProductIcon name="rw" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["android"]}
                  expanded={rwAndroidOpen}
                  onToggle={() => setRwAndroidOpen((open) => !open)}
                  toggleTestId="toggle-rw-android-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.rwAndroid.subtitle")}
                  </p>
                  <StepCard number={rwAndroidStep++} title={t("report.rwAndroid.sysReq.title")} expanded={rwAndroidOpen}>
                    <StepBody>{t("report.rwAndroid.sysReq.description")}</StepBody>
                    <ReportLink href={rwAndroidSystemRequirementsHref} testId="link-rw-android-system-requirements">
                      {t("report.rwAndroid.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={rwAndroidStep++} title={t("report.rwAndroid.download.title")} expanded={rwAndroidOpen}>
                    <StepBody>{t("report.rwAndroid.download.description")}</StepBody>
                    <ReportLink href={rwAndroidDownloadHref} testId="link-rw-android-download">
                      {t("report.rwAndroid.download.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={rwAndroidStep++} title={t("report.rwAndroid.deploy.title")} expanded={rwAndroidOpen}>
                    <StepBody>{t("report.rwAndroid.deploy.description")}</StepBody>
                    <ReportLink href={rwAndroidDeploymentGuideHref} testId="link-rw-android-deployment-guide">
                      {t("report.rwAndroid.deploy.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard number={rwAndroidStep++} title={t("report.rwAndroid.userGuide.title")} expanded={rwAndroidOpen}>
                    <StepBody>{t("report.rwAndroid.userGuide.description")}</StepBody>
                    <ReportLink href={downloads.rwAndroidUserGuide} testId="link-rw-android-user-guide">
                      {t("report.rwAndroid.userGuide.link")}
                    </ReportLink>
                  </StepCard>
                </div>
              </ReportSection>
            ) : null}

            {hasMv && mvVersions.includes("win") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.mvWindows.title")}
                  icon={<ProductIcon name="mindview" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["windows"]}
                  expanded={mvWinOpen}
                  onToggle={() => setMvWinOpen((open) => !open)}
                  toggleTestId="toggle-mv-win-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.mvWindows.subtitle")}
                  </p>
                  <StepCard number={mvWinStep++} title={t("report.mvWindows.sysReq.title")} expanded={mvWinOpen}>
                    <StepBody>{t("report.mvWindows.sysReq.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(19, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245409773331-MindView-Windows-System-Requirements")}
                      testId="link-mv-win-system-requirements"
                    >
                      {t("report.mvWindows.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={mvWinStep++}
                    title={t("report.mvWindows.download.title")}
                    expanded={mvWinOpen}
                    maxHeightClass="max-h-64"
                  >
                    <StepBody>{t("report.mvWindows.download.description")}</StepBody>
                    <ReportLink href={mindviewWindowsDownloadHref} testId="download-mv-win-msi">
                      {t("report.mvWindows.download.link")}
                    </ReportLink>
                    <div className="pt-1 border-t border-ink-5">
                      <p className="text-[12px] text-foreground/50 leading-relaxed mb-1.5">
                        {t("report.mvWindows.download.exeNote")}
                      </p>
                      <ReportLink
                        href={resolveLink(20, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245409659027-How-do-I-extract-the-MSI-file-from-the-MindView-installer")}
                        testId="link-mv-win-exe-to-msi"
                      >
                        {t("report.mvWindows.download.extractLink")}
                      </ReportLink>
                      <ReportLink
                        href={resolveLink(21, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245636111763-MindView-9-til-Windows-MSI-installations-og-udrulningsvejledning")}
                        testId="link-mv-win-msi-deploy-guide"
                        className="inline-flex items-center gap-1.5 mt-1 text-xs font-medium text-ink-80 hover:text-ink transition-colors"
                      >
                        {t("report.mvWindows.download.deployGuide")}
                      </ReportLink>
                    </div>
                  </StepCard>
                  {answers.login_method === "auto" ? (
                    <StepCard
                      number={mvWinStep++}
                      title={t("report.mvWindows.autoSignIn.title")}
                      expanded={mvWinOpen}
                      maxHeightClass="max-h-64"
                    >
                      <StepBody>{t("report.mvWindows.autoSignIn.description")}</StepBody>
                      <AmberAlert>
                        <p>{t("report.mvWindows.autoSignIn.productCodeNote")}</p>
                      </AmberAlert>
                      <ReportLink
                        href={resolveLink(22, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245589897107-Konfigurer-automatisk-login-for-brugere")}
                        testId="link-mv-win-auto-signin"
                      >
                        {t("report.mvWindows.autoSignIn.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {answers.network_restricted === "yes" ? (
                    <StepCard number={mvWinStep++} title={t("report.mvWindows.allowlist.title")} expanded={mvWinOpen}>
                      <StepBody>{t("report.mvWindows.allowlist.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(23, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245438744467-URL-Allowlist-used-by-MindView")}
                        testId="link-mv-win-url-allowlist"
                      >
                        {t("report.mvWindows.allowlist.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {answers.mv_win_restrict_online_services === "yes" ? (
                    <StepCard number={mvWinStep++} title={t("report.mvWindows.restrict.title")} expanded={mvWinOpen}>
                      <StepBody>{t("report.mvWindows.restrict.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(24, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245594584467-Deaktivering-af-MindView-online-tjenester-og-AI-via-admin-portalen")}
                        testId="link-mv-win-restrict-guide"
                      >
                        {t("report.mvWindows.restrict.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  <StepCard
                    number={mvWinStep++}
                    title={
                      <>
                        {t("report.mvWindows.sso.title")}
                        <OptionalBadge label={t("report.optional")} />
                      </>
                    }
                    expanded={mvWinOpen}
                    maxHeightClass="max-h-52"
                  >
                    <StepBody>{t("report.mvWindows.sso.description")}</StepBody>
                    <ReportLink
                        href={resolveLink(25, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245595361043-Administrering-af-brugere-gennem-Active-Directory-Entra-ID")}
                      testId="link-mv-win-sso-guide"
                    >
                      {t("report.mvWindows.sso.link")}
                    </ReportLink>
                  </StepCard>
                </div>
              </ReportSection>
            ) : null}

            {hasMv && mvVersions.includes("mac") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.mvMac.title")}
                  icon={<ProductIcon name="mindview" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["macos"]}
                  expanded={mvMacOpen}
                  onToggle={() => setMvMacOpen((open) => !open)}
                  toggleTestId="toggle-mv-mac-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.mvMac.subtitle")}
                  </p>
                  <StepCard number={mvMacStep++} title={t("report.mvMac.sysReq.title")} expanded={mvMacOpen}>
                    <StepBody>{t("report.mvMac.sysReq.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(26, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245455530899-Systemkrav")}
                      testId="link-mv-mac-system-requirements"
                    >
                      {t("report.mvMac.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={mvMacStep++}
                    title={t("report.mvMac.download.title")}
                    expanded={mvMacOpen}
                    maxHeightClass="max-h-64"
                  >
                    <StepBody>{t("report.mvMac.download.description")}</StepBody>
                    <ReportLink href={mindviewMacDownloadHref} testId="download-mv-mac">
                      {t("report.mvMac.download.link")}
                    </ReportLink>
                    <p className="text-[12px] text-foreground/50 leading-relaxed">
                      {t("report.mvMac.download.deployGuideNote")}
                    </p>
                    <ReportLink
                      href={resolveLink(27, "https://everwayeu.zendesk.com/hc/en-gb/articles/54412741924499-MindView-9-for-Mac-Deployment-Installation-Guide")}
                      testId="link-mv-mac-deploy-guide"
                    >
                      {t("report.mvMac.download.deployGuide")}
                    </ReportLink>
                  </StepCard>
                  {answers.network_restricted === "yes" ? (
                    <StepCard number={mvMacStep++} title={t("report.mvMac.allowlist.title")} expanded={mvMacOpen}>
                      <StepBody>{t("report.mvMac.allowlist.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(23, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245438744467-URL-Allowlist-used-by-MindView")}
                        testId="link-mv-mac-url-allowlist"
                      >
                        {t("report.mvMac.allowlist.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {answers.login_method === "auto" ? (
                    <StepCard
                      number={mvMacStep++}
                      title={t("report.mvMac.autoSignIn.title")}
                      expanded={mvMacOpen}
                      maxHeightClass="max-h-64"
                    >
                      <StepBody>{t("report.mvMac.autoSignIn.description")}</StepBody>
                      <AmberAlert>
                        <p>{t("report.mvMac.autoSignIn.productCodeNote")}</p>
                      </AmberAlert>
                      <ReportLink
                        href={resolveLink(30, "https://everwayeu.zendesk.com/hc/en-gb/articles/54412953865491-MindView-Mac-configure-automatic-login-for-users")}
                        testId="link-mv-mac-auto-signin"
                      >
                        {t("report.mvMac.autoSignIn.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  <StepCard
                    number={mvMacStep++}
                    title={
                      <>
                        {t("report.mvMac.sso.title")}
                        <OptionalBadge label={t("report.optional")} />
                      </>
                    }
                    expanded={mvMacOpen}
                    maxHeightClass="max-h-52"
                  >
                    <StepBody>{t("report.mvMac.sso.description")}</StepBody>
                    <ReportLink
                        href={resolveLink(25, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245595361043-Administrering-af-brugere-gennem-Active-Directory-Entra-ID")}
                      testId="link-mv-mac-sso-guide"
                    >
                      {t("report.mvMac.sso.link")}
                    </ReportLink>
                  </StepCard>
                  {answers.mv_win_restrict_online_services === "yes" ? (
                    <StepCard number={mvMacStep++} title={t("report.mvMac.restrict.title")} expanded={mvMacOpen}>
                      <StepBody>{t("report.mvMac.restrict.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(24, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245594584467-Deaktivering-af-MindView-online-tjenester-og-AI-via-admin-portalen")}
                        testId="link-mv-mac-restrict-guide"
                      >
                        {t("report.mvMac.restrict.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                </div>
              </ReportSection>
            ) : null}

            {hasMv && mvVersions.includes("online") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.mvOnline.title")}
                  icon={<ProductIcon name="mindview" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["mindview_online"]}
                  expanded={mvOnlineOpen}
                  onToggle={() => setMvOnlineOpen((open) => !open)}
                  toggleTestId="toggle-mv-online-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.mvOnline.subtitle")}
                  </p>
                  <StepCard number={mvOnlineStep++} title={t("report.mvOnline.sysReq.title")} expanded={mvOnlineOpen}>
                    <StepBody>{t("report.mvOnline.sysReq.description")}</StepBody>
                  </StepCard>
                  <StepCard number={mvOnlineStep++} title={t("report.mvOnline.access.title")} expanded={mvOnlineOpen}>
                    <StepBody>{t("report.mvOnline.access.description")}</StepBody>
                    <ReportLink href={downloads.mindviewOnline} testId="link-mv-online-access">
                      {t("report.mvOnline.access.link")}
                    </ReportLink>
                  </StepCard>
                  {answers.network_restricted === "yes" ? (
                    <StepCard number={mvOnlineStep++} title={t("report.mvOnline.allowlist.title")} expanded={mvOnlineOpen}>
                      <StepBody>{t("report.mvOnline.allowlist.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(23, "https://everwayeu.zendesk.com/hc/en-gb/articles/53245438744467-URL-Allowlist-used-by-MindView")}
                        testId="link-mv-online-allowlist"
                      >
                        {t("report.mvOnline.allowlist.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                </div>
              </ReportSection>
            ) : null}

            {hasMv && mvVersions.includes("assist") && mvAssistPlatforms.includes("android") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.mvAssistAndroid.title")}
                  icon={<ProductIcon name="mindview" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["android"]}
                  expanded={mvAssistAndroidOpen}
                  onToggle={() => setMvAssistAndroidOpen((open) => !open)}
                  toggleTestId="toggle-mv-assist-android-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.mvAssistAndroid.subtitle")}
                  </p>
                  <StepCard
                    number={mvAssistAndroidStep++}
                    title={t("report.mvAssistAndroid.sysReq.title")}
                    expanded={mvAssistAndroidOpen}
                  >
                    <StepBody>{t("report.mvAssistAndroid.sysReq.description")}</StepBody>
                    <ReportLink
                      href={mindviewAssistAndroidSystemRequirementsHref}
                      testId="link-mv-assist-android-system-requirements"
                    >
                      {t("report.mvAssistAndroid.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={mvAssistAndroidStep++}
                    title={t("report.mvAssistAndroid.download.title")}
                    expanded={mvAssistAndroidOpen}
                  >
                    <StepBody>{t("report.mvAssistAndroid.download.description")}</StepBody>
                    <ReportLink
                      href={mindviewAssistAndroidDownloadHref}
                      testId="link-mv-assist-android-download"
                    >
                      {t("report.mvAssistAndroid.download.link")}
                    </ReportLink>
                  </StepCard>
                  {answers.network_restricted === "yes" ? (
                    <StepCard
                      number={mvAssistAndroidStep++}
                      title={t("report.mvWindows.allowlist.title")}
                      expanded={mvAssistAndroidOpen}
                    >
                      <StepBody>{t("report.mvWindows.allowlist.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(
                          23,
                          "https://everwayeu.zendesk.com/hc/en-gb/articles/53245438744467-URL-Allowlist-used-by-MindView",
                        )}
                        testId="link-mv-assist-android-allowlist"
                      >
                        {t("report.mvWindows.allowlist.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                </div>
              </ReportSection>
            ) : null}

            {hasMv && mvVersions.includes("assist") && mvAssistPlatforms.includes("ios") ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.mvAssistIos.title")}
                  icon={<ProductIcon name="mindview" className="w-7 h-7 rounded shrink-0" />}
                  platformIcons={["ios"]}
                  expanded={mvAssistIosOpen}
                  onToggle={() => setMvAssistIosOpen((open) => !open)}
                  toggleTestId="toggle-mv-assist-ios-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.mvAssistIos.subtitle")}
                  </p>
                  <StepCard
                    number={mvAssistIosStep++}
                    title={t("report.mvAssistIos.sysReq.title")}
                    expanded={mvAssistIosOpen}
                  >
                    <StepBody>{t("report.mvAssistIos.sysReq.description")}</StepBody>
                    <ReportLink
                      href={mindviewAssistIosSystemRequirementsHref}
                      testId="link-mv-assist-ios-system-requirements"
                    >
                      {t("report.mvAssistIos.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  <StepCard
                    number={mvAssistIosStep++}
                    title={t("report.mvAssistIos.download.title")}
                    expanded={mvAssistIosOpen}
                  >
                    <StepBody>{t("report.mvAssistIos.download.description")}</StepBody>
                    <ReportLink
                      href={mindviewAssistIosDownloadHref}
                      testId="link-mv-assist-ios-download"
                    >
                      {t("report.mvAssistIos.download.link")}
                    </ReportLink>
                  </StepCard>
                  {answers.network_restricted === "yes" ? (
                    <StepCard
                      number={mvAssistIosStep++}
                      title={t("report.mvWindows.allowlist.title")}
                      expanded={mvAssistIosOpen}
                    >
                      <StepBody>{t("report.mvWindows.allowlist.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(
                          23,
                          "https://everwayeu.zendesk.com/hc/en-gb/articles/53245438744467-URL-Allowlist-used-by-MindView",
                        )}
                        testId="link-mv-assist-ios-allowlist"
                      >
                        {t("report.mvWindows.allowlist.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                </div>
              </ReportSection>
            ) : null}

            {hasTasks ? (
              <ReportSection>
                <ReportSectionHeader
                  title={t("report.tasks.title")}
                  icon={<ProductIcon name="tasks" className="w-7 h-7 rounded shrink-0" />}
                  expanded={tasksOpen}
                  onToggle={() => setTasksOpen((open) => !open)}
                  toggleTestId="toggle-tasks-expand"
                  expandLabel={t("report.expandAll")}
                  collapseLabel={t("report.collapseAll")}
                />
                <div className="p-5 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/50 font-medium">
                    {t("report.tasks.subtitle")}
                  </p>
                  <StepCard number={tasksStep++} title={t("report.tasks.sysReq.title")} expanded={tasksOpen}>
                    <StepBody>{t("report.tasks.sysReq.description")}</StepBody>
                    <ReportLink
                      href={resolveLink(
                        32,
                        "https://everwayeu.zendesk.com/hc/en-gb/articles/55772693472787-Tasks-System-requirements",
                      )}
                      testId="link-tasks-system-requirements"
                    >
                      {t("report.tasks.sysReq.link")}
                    </ReportLink>
                  </StepCard>
                  {tasksOs.includes("chromeos") ? (
                    <StepCard number={tasksStep++} title={t("report.tasks.gettingStarted.title")} expanded={tasksOpen}>
                      <StepBody>{t("report.tasks.gettingStarted.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(28, "https://everwayeu.zendesk.com/hc/en-gb/articles/53002966822803-Ops%C3%A6tnings-og-installationsvejledning")}
                        testId="link-tasks-setup-guide"
                      >
                        {t("report.tasks.gettingStarted.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {tasksOs.includes("win") ? (
                    <StepCard
                      number={tasksStep++}
                      title={t("report.tasks.windowsDownload.title")}
                      expanded={tasksOpen}
                      maxHeightClass="max-h-96"
                    >
                      <StepBody>{t("report.tasks.windowsDownload.description")}</StepBody>
                      {answers.tasks_intune === "yes" ? (
                        <AmberAlert title={t("report.tasks.windowsDownload.intune.title")}>
                          <p>{t("report.tasks.windowsDownload.intune.description")}</p>
                          <ReportLink
                            href={resolveLink(34, "https://everwayeu.zendesk.com/hc/en-gb/articles/56329900406803")}
                            testId="link-tasks-windows-intune"
                            className="inline-flex items-center gap-1.5 mt-1 text-xs font-semibold text-amber-300/90 hover:text-amber-200 transition-colors"
                          >
                            {t("report.tasks.windowsDownload.intune.link")}
                          </ReportLink>
                        </AmberAlert>
                      ) : null}
                      <ReportLink href={tasksWindowsDownloadHref} testId="link-tasks-windows-download">
                        {t("report.tasks.windowsDownload.link")}
                      </ReportLink>
                      <div className="border-l-2 border-amber-500/40 pl-3 space-y-1">
                        <ReportLink
                          href={resolveLink(35, "https://everwayeu.zendesk.com/hc/en-gb/articles/56329900412563")}
                          testId="link-tasks-windows-msi-parameters"
                          className="flex items-center gap-1.5 text-xs font-medium text-foreground/50 hover:text-ink-70 transition-colors"
                        >
                          {t("report.tasks.windowsDownload.msiDocsLink")}
                        </ReportLink>
                        <p className="text-[11px] text-amber-300/85 italic leading-relaxed">
                          {t("report.tasks.windowsDownload.msiDocsWarning")}
                        </p>
                      </div>
                    </StepCard>
                  ) : null}
                  {tasksOs.includes("mac") ? (
                    <StepCard
                      number={tasksStep++}
                      title={t("report.tasks.macDownload.title")}
                      expanded={tasksOpen}
                    >
                      <StepBody>{t("report.tasks.macDownload.description")}</StepBody>
                      <ReportLink href={tasksMacDownloadHref} testId="link-tasks-mac-download">
                        {t("report.tasks.macDownload.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {answers.network_restricted === "yes" ? (
                    <StepCard number={tasksStep++} title={t("report.tasks.allowlist.title")} expanded={tasksOpen}>
                      <StepBody>{t("report.tasks.allowlist.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(
                          31,
                          "https://everwayeu.zendesk.com/hc/en-gb/articles/56217587897619-Allowlist-URLs-and-Domains-for-Tasks",
                        )}
                        testId="link-tasks-allowlist"
                      >
                        {t("report.tasks.allowlist.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                  {tasksOs.includes("win") && answers.tasks_start_menu === "yes" ? (
                    <StepCard number={tasksStep++} title={t("report.tasks.rollOut.title")} expanded={tasksOpen}>
                      <StepBody>{t("report.tasks.rollOut.description")}</StepBody>
                      <ReportLink
                        href={resolveLink(29, "https://everwayeu.zendesk.com/hc/en-gb/articles/53246693597459-Installation-af-Tasks-p%C3%A5-et-Windows-netv%C3%A6rk")}
                        testId="link-tasks-start-menu"
                      >
                        {t("report.tasks.rollOut.link")}
                      </ReportLink>
                    </StepCard>
                  ) : null}
                </div>
              </ReportSection>
            ) : null}

            <ReportSection>
              <div className="px-5 py-4 border-b border-ink-10 flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-ink-5 border border-ink-10 flex items-center justify-center shrink-0">
                  <LifeBuoyIcon className="w-4 h-4 text-ink-60" />
                </div>
                <div className="flex-grow">
                  <h3 className="font-semibold text-base text-foreground">{t("report.support.title")}</h3>
                  <p className="text-[11px] text-muted-foreground/50 mt-0.5">{t("report.support.subtitle")}</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div className="group relative rounded-lg border border-ink-10 hover:border-ink-25 transition-all duration-200">
                  <div className="flex items-center gap-3 px-4 py-3 cursor-pointer">
                    <div className="w-8 h-8 rounded-lg bg-ink-6 flex items-center justify-center shrink-0">
                      <BookIcon className="w-4 h-4 text-ink-50" />
                    </div>
                    <p className="font-medium text-sm flex-grow flex items-center gap-2">
                      {t("report.support.academy.title")}
                    </p>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-ink-70 transition-colors shrink-0" />
                  </div>
                  <div className="overflow-hidden max-h-0 group-hover:max-h-40 transition-all duration-300 ease-in-out">
                    <div className="px-4 pb-3 ml-11">
                      <StepBody>{t("report.support.academy.description")}</StepBody>
                      <ReportLink
                        href={resolveString("academy_url", downloads.academy)}
                        testId="link-academy"
                      >
                        {t("report.support.academy.link")}
                      </ReportLink>
                    </div>
                  </div>
                </div>

                <div className="group relative rounded-lg border border-ink-10 hover:border-ink-25 transition-all duration-200">
                  <div className="flex items-center gap-3 px-4 py-3 cursor-pointer">
                    <div className="w-8 h-8 rounded-lg bg-ink-6 flex items-center justify-center shrink-0">
                      <LibraryIcon className="w-4 h-4 text-ink-50" />
                    </div>
                    <p className="font-medium text-sm flex-grow">{t("report.support.kb.title")}</p>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-ink-70 transition-colors shrink-0" />
                  </div>
                  <div className="overflow-hidden max-h-0 group-hover:max-h-40 transition-all duration-300 ease-in-out">
                    <div className="px-4 pb-3 ml-11">
                      <StepBody>{t("report.support.kb.description")}</StepBody>
                      <ReportLink
                        href={resolveString("kb_url", "https://everwayeu.zendesk.com/hc/en-gb")}
                        testId="link-knowledgebase"
                      >
                        {t("report.support.kb.link")}
                      </ReportLink>
                    </div>
                  </div>
                </div>

                <div className="group relative rounded-lg border border-ink-10 hover:border-ink-25 transition-all duration-200">
                  <div className="flex items-center gap-3 px-4 py-3 cursor-pointer">
                    <div className="w-8 h-8 rounded-lg bg-ink-6 flex items-center justify-center shrink-0">
                      <HeadsetIcon className="w-4 h-4 text-ink-50" />
                    </div>
                    <p className="font-medium text-sm flex-grow">{t("report.support.contact.title")}</p>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-ink-70 transition-colors shrink-0" />
                  </div>
                  <div className="overflow-hidden max-h-0 group-hover:max-h-40 transition-all duration-300 ease-in-out">
                    <div className="px-4 pb-3 ml-11 space-y-2">
                      <a
                        href={`mailto:${resolveString("support_email", t("report.support.contact.email"))}`}
                        className="flex items-center gap-3 p-2.5 rounded-lg border border-ink-10 hover:border-ink-25 hover:bg-ink-2 transition-all duration-200"
                        data-testid="link-support-email"
                      >
                        <MailIcon className="w-3.5 h-3.5 text-ink-40 shrink-0" />
                        <span className="text-[13px] text-foreground/70">
                          {resolveString("support_email", t("report.support.contact.email"))}
                        </span>
                      </a>
                      {resolveString("support_phone", t("report.support.contact.phone")) ? (
                        <a
                          href={`tel:${resolveString("support_phone", t("report.support.contact.phone")).replace(/\s/g, "")}`}
                          className="flex items-center gap-3 p-2.5 rounded-lg border border-ink-10 hover:border-ink-25 hover:bg-ink-2 transition-all duration-200"
                          data-testid="link-support-phone"
                        >
                          <PhoneIcon className="w-3.5 h-3.5 text-ink-40 shrink-0" />
                          <span className="text-[13px] text-foreground/70">
                            {resolveString("support_phone", t("report.support.contact.phone"))}
                          </span>
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="group relative rounded-lg border border-ink-10 hover:border-ink-25 transition-all duration-200">
                  <div className="flex items-center gap-3 px-4 py-3 cursor-pointer">
                    <div className="w-8 h-8 rounded-lg bg-ink-6 flex items-center justify-center shrink-0">
                      <ActivityIcon className="w-4 h-4 text-ink-50" />
                    </div>
                    <p className="font-medium text-sm flex-grow">{t("report.support.status.title")}</p>
                    <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-ink-70 transition-colors shrink-0" />
                  </div>
                  <div className="overflow-hidden max-h-0 group-hover:max-h-40 transition-all duration-300 ease-in-out">
                    <div className="px-4 pb-3 ml-11">
                      <StepBody>{t("report.support.status.description")}</StepBody>
                      <ReportLink
                        href={resolveString("status_page_url", "https://status.everway.com/")}
                        testId="link-product-status"
                      >
                        {t("report.support.status.link")}
                      </ReportLink>
                    </div>
                  </div>
                </div>
              </div>
            </ReportSection>
          </div>
        </div>

        <div className="pt-6 pb-4 mt-auto space-y-4 print-hidden">
          <div className="grid grid-cols-3 gap-2">
            <div className="relative group/pdf">
              <button
                type="button"
                onClick={printReport}
                className="w-full px-4 py-2.5 text-sm font-medium text-foreground/80 rounded-full border border-ink-15 bg-transparent hover:bg-ink-5 hover:border-ink-30 transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                data-testid="button-download-pdf"
              >
                <FileTextIcon className="w-4 h-4" /> {t("report.buttons.saveAsPdf")}
              </button>
              <span className="pointer-events-none absolute z-50 hidden group-hover/pdf:block bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs rounded-md border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-xs text-white shadow-lg">
                {t("report.buttons.pdfTooltip")}
              </span>
            </div>
            {config.app.features.emailReport ? (
              <button
                type="button"
                onClick={emailReport}
                className="px-4 py-2.5 text-sm font-medium text-foreground/80 rounded-full border border-ink-15 bg-transparent hover:bg-ink-5 hover:border-ink-30 transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
                data-testid="button-email-report"
              >
                <MailIcon className="w-4 h-4" /> {t("report.buttons.email")}
              </button>
            ) : null}
            <button
              type="button"
              onClick={printReport}
              className="px-4 py-2.5 text-sm font-medium text-foreground/80 rounded-full border border-ink-15 bg-transparent hover:bg-ink-5 hover:border-ink-30 transition-all duration-200 cursor-pointer flex items-center justify-center gap-2"
              data-testid="button-print-report"
            >
              <PrinterIcon className="w-4 h-4" /> {t("report.buttons.print")}
            </button>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => {
                resetAnswers();
                navigate("/");
              }}
              className="px-5 py-2.5 text-sm font-medium text-muted-foreground rounded-full border border-transparent hover:text-foreground hover:border-ink-15 transition-all duration-200 cursor-pointer"
              data-testid="button-wizard-home"
            >
              {t("report.buttons.startOver")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
